import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Resolver, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { GroupBase } from "react-select";

import useUserStore from "@store/useUserStore";
import useDialogStore from "@store/useDialogStore";
import { useUserpilot } from "@platform/hooks/useUserpilot";
import { useSwitches } from "@hooks/useSwitches";
import { useEscapeKey } from "@hooks/useEscapeKey";
import { useDealStages } from "../hooks/useDealStages";
import { useUsers } from "@components/TeamList/hooks/useUsers";
import { useGetThreadAttachments } from "@screens/sync-email/hooks/useGetThreadAttachments";
import { useGetCompany } from "@screens/company/hooks/useGetCompany";
import { collapseToDeepestThemes } from "@components/SourcingThemes/utils";
import { DealStageStatus, Source } from "@enums/common.enum";
import { CreateDealRequest } from "@app-types/requests.type";
import { IThemeOption } from "@interfaces/sourcing.interface";
import { IAttribute, IAttributeFieldValue } from "@interfaces/attribute.interface";
import { IEmailAttachment } from "@interfaces/email.interface";
import { isAttachmentPending, isSupportedDealDocAttachment } from "@utils/attachment.util";
import { Deal, UserForDropdown } from "@app-types/common.type";
import {
  ICompanySelectOption,
  SourceCompanyOption,
  PersonValueItem,
  TypeOption,
} from "@components/AddNewEntity/Deal/Selects";
import {
  CompanyFormField,
  SourceCompanyFormField,
  SourcePersonFormField,
  SourceTypeFormField,
} from "@components/AddNewEntity/Deal/FormFields";
import {
  getCompanyData,
  stagesToGroupedDropdownOptions,
} from "@components/AddNewEntity/Deal/utils";
import { UPLOAD_ERROR_MESSAGES } from "@config/messages";
import ToastService from "@services/toast.service";
import { ApiError } from "@config/apiClient";
import { DisconnectedError } from "@app-types/errors";
import { convertUsersToUsersForDropdown } from "@utils/users.util";
import { dealSchema, IAttributeValidationContext } from "../schema";
import {
  TeamField,
  IPipelineStageSelectOption,
  PipelineStageFormField,
  SourcingThemeFormField,
} from "@components/AddNewEntity";
import {
  AttributesForm,
  useAllAttributes,
  useCommonAttributeSync,
  useCompanyAttributes,
  useDealAttributes,
  useDefaultAttributeValues,
  filterAndProcessAttributeValues,
  generateDefaultAttributeFieldValues,
  getCommonAttributeIds,
  getAttributeValueUpdates,
} from "@components/Attributes";
import { useUpdateCompany } from "@screens/company/hooks/useUpdateCompany";
import { openDealPage } from "@utils/navigation.util";
import { CollapsibleSection } from "@ui/CollapsibleSection";
import { BaseDialog } from "@ui/Dialogs/BaseDialog";
import { ControlledInput } from "@ui/FormElements";
import { CIMUploader } from "./CIMUploader";
import { Button } from "@ui/Button";

export interface ICreateDealForm {
  title: string;
  stage: IPipelineStageSelectOption | null;
  company: ICompanySelectOption | null;
  source_type: TypeOption | null;
  source_company: SourceCompanyOption | null;
  source_people: {
    person: PersonValueItem[];
    user: PersonValueItem[];
  };
  users: UserForDropdown[];
  themes: IThemeOption[];
  documents: File[];
  attachments: IEmailAttachment[];
  // The selected company's attributes; shared ones mirror into the deal's.
  attribute_values: IAttributeFieldValue[];
  // The deal's own attributes; submitted with the deal.
  deal_attribute_values: IAttributeFieldValue[];
}

interface Props {
  query: string;
  onSave: (data: CreateDealRequest) => Promise<Deal>;
  sourceItem: string;
  sourcePlatform: Source;
}

const REFETCH_INTERVAL = 3000;

export function AddNewDealDialog({ query, onSave, sourceItem, sourcePlatform }: Props) {
  const { track } = useUserpilot();
  const { isAvailable } = useSwitches();
  const isDealSourceAvailable = isAvailable("DEAL_SOURCE");
  const { isOpen, closeDialog } = useDialogStore();
  const titleInputRef = useRef<HTMLInputElement>(null);
  // Read by the schema's attribute_values rules; a ref because the attribute list follows the
  // company selected in this very form, so it isn't known when useForm runs.
  const attributesRef = useRef<IAttribute[]>([]);
  const dealAttributesRef = useRef<IAttribute[]>([]);

  const { control, handleSubmit, formState, setValue, reset, getValues, watch } =
    useForm<ICreateDealForm>({
      mode: "all",
      defaultValues: {
        title: query || "",
        stage: null,
        company: null,
        source_type: null,
        source_company: null,
        source_people: { person: [], user: [] },
        users: [],
        themes: [],
        documents: [],
        attachments: [],
        attribute_values: [],
        deal_attribute_values: [],
      },
      reValidateMode: "onChange",
      context: {
        getAttributes: () => attributesRef.current,
        getDealAttributes: () => dealAttributesRef.current,
      } as IAttributeValidationContext,
      resolver: yupResolver(dealSchema) as Resolver<ICreateDealForm>,
    });

  const formData = useWatch({ control });

  const firstInputRef = useRef(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dealError, setDealError] = useState<ReactNode | null>(null);
  const disableSubmitButton = isSubmitting || !!dealError;

  const { activeUser } = useUserStore();
  const { data: allUsers } = useUsers();
  const { data: stages } = useDealStages();
  const { data: threadAttachmentsGroupedByEmail, refetch: refetchThreadAttachments } =
    useGetThreadAttachments();

  const companyField = useWatch({ control, name: "company" });

  // Meridian companies carry their id as `value`; global-DB suggestions carry a website plus a
  // `suggestion` payload and don't exist in Meridian yet, so they have no themes to copy.
  const selectedMeridianCompanyId =
    companyField && !companyField.suggestion ? companyField.value : undefined;

  const { data: selectedCompany } = useGetCompany(selectedMeridianCompanyId);

  // An existing company waits for its fetch: seeding earlier would overwrite edits made in the gap.
  const isUnsavedCompany = !!companyField?.suggestion || !!companyField?.is_manual;
  const showCompanyAttributes = isUnsavedCompany || !!selectedCompany;

  // The deal form surfaces the selected company's attributes alongside the deal's own; nothing
  // is written until the deal is created, as in the company creation flow.
  const { mutateAsync: updateCompany } = useUpdateCompany();
  const { data: companyAttributes } = useCompanyAttributes({ type: selectedCompany?.type?.id });
  const { data: dealAttributes } = useDealAttributes();
  const { data: allAttributes = [] } = useAllAttributes();
  const effectiveDeal =
    (isAvailable("MULTIPLE_ACTIVE_DEALS")
      ? selectedCompany?.primary_deal
      : selectedCompany?.active_deal) ?? null;
  const commonAttributeIds = useMemo(() => getCommonAttributeIds(allAttributes), [allAttributes]);

  useEffect(() => {
    attributesRef.current = showCompanyAttributes ? companyAttributes : [];
    dealAttributesRef.current = dealAttributes;
  }, [showCompanyAttributes, companyAttributes, dealAttributes]);

  useDefaultAttributeValues<ICreateDealForm>({
    name: "attribute_values",
    control,
    setValue,
    attributes: showCompanyAttributes ? companyAttributes : [],
    existingValues: selectedCompany?.attribute_values,
  });

  // The deal doesn't exist yet, so its fields start empty and are filled by the sync below.
  useDefaultAttributeValues<ICreateDealForm>({
    name: "deal_attribute_values",
    control,
    setValue,
    attributes: dealAttributes,
  });

  useCommonAttributeSync<ICreateDealForm>({
    watch,
    getValues,
    setValue,
    companyName: "attribute_values",
    dealName: "deal_attribute_values",
    commonAttributeIds,
    enabled: showCompanyAttributes,
  });

  // Append the selected company's sourcing themes to the new deal (MER-8104). Keyed on the
  // resolved company id so switching companies appends once per company, not on every render.
  const lastCopiedCompanyId = useRef<string | undefined>(undefined);

  useEffect(() => {
    // Don't hydrate themes from the company when sourcing themes are turned off for this org.
    if (!isAvailable("THEMATIC_SOURCING")) return;
    if (!selectedCompany?.id || selectedCompany.id === lastCopiedCompanyId.current) return;
    lastCopiedCompanyId.current = selectedCompany.id;

    const companyThemes = selectedCompany.themes ?? [];
    if (!companyThemes.length) return; // nothing to copy — keep any themes already added

    // Merge into the themes the user already picked, deduping by id, then collapse each branch to
    // its deepest node so we never keep both an ancestor (e.g. level 1) and its descendant nested
    // under it. May still push the total over MAX_THEMES — validation then forces a trim-down.
    const existingThemes = getValues("themes") ?? [];
    const existingThemeIds = new Set(existingThemes.map((theme) => theme.id));
    const newThemes = companyThemes.filter((theme) => !existingThemeIds.has(theme.id));
    if (!newThemes.length) return;

    setValue("themes", collapseToDeepestThemes([...existingThemes, ...newThemes]), {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [selectedCompany, getValues, setValue, isAvailable]);

  const stageOptions: GroupBase<IPipelineStageSelectOption>[] | IPipelineStageSelectOption[] =
    useMemo(() => {
      return stagesToGroupedDropdownOptions(stages?.results || [], !!companyField);
    }, [stages, companyField]);

  const userOptions: UserForDropdown[] = useMemo(
    () => convertUsersToUsersForDropdown(allUsers?.results, activeUser),
    [allUsers, activeUser]
  );

  const threadAttachments = useMemo((): IEmailAttachment[] => {
    if (!threadAttachmentsGroupedByEmail?.results?.length) return [];

    const flatItems = [];
    threadAttachmentsGroupedByEmail?.results.map((group) => flatItems.push(...group.attachments));

    return flatItems.filter(isSupportedDealDocAttachment);
  }, [threadAttachmentsGroupedByEmail]);

  const isProcessingDocuments = useMemo(() => {
    return threadAttachments.some(isAttachmentPending);
  }, [threadAttachments]);

  const handleSave = async (data: ICreateDealForm) => {
    if (disableSubmitButton) return;

    setIsSubmitting(true);

    const stage = data.stage || null;
    const sourcePeople = data.source_people || { person: [], user: [] };
    const sourceCompany = data.source_company || null;

    const sourceCompanyData = sourceCompany && "data" in sourceCompany ? sourceCompany.data : null;

    const peopleIds = sourcePeople.person.map((p) => p.id) as string[];
    const userIds = sourcePeople.user.map((u) => u.id) as string[];

    // The company is saved first: the deal only carries its own values, so leaving this until
    // afterwards would drop the company's edits — visible as stale values when the deal is
    // removed and this form reopened.
    const storedCompanyValues = generateDefaultAttributeFieldValues(
      companyAttributes,
      selectedCompany?.attribute_values
    );
    const changedCompanyValues = getAttributeValueUpdates(
      data.attribute_values,
      storedCompanyValues,
      new Set(companyAttributes.map((attribute) => attribute.id))
    );

    // Shared attributes live on the company too, so saving a changed one lands on both — the
    // mirror image of the company form's "Change applied to Deal".
    const changedShared = getAttributeValueUpdates(
      data.attribute_values,
      storedCompanyValues,
      commonAttributeIds
    );

    try {
      if (selectedCompany && changedCompanyValues.length) {
        await updateCompany({
          id: selectedCompany.id,
          hasSharedAttributeUpdates: changedShared.length > 0,
          attribute_values: filterAndProcessAttributeValues(
            data.attribute_values,
            companyAttributes,
            { allowEmptyValues: true }
          ),
        });
      }

      await onSave({
        source: sourcePlatform,
        stage: stage?.value ?? null,
        title: data.title,
        team: data.users?.map((user) => user.id) || [],
        theme_ids: data.themes?.map((theme) => theme.id) ?? [],
        ...getCompanyData(data, companyAttributes),
        deal_source: {
          source: sourcePlatform,
          type: data.source_type?.value ?? null,
          ...(sourceCompanyData
            ? { external_company: sourceCompanyData }
            : { company: sourceCompany?.value }),
          people: peopleIds,
          users: userIds,
        },
        files: formData?.documents || [],
        document_file: formData.attachments[0]?.document?.id || null,
        // new deal inherits the company's currency, otherwise uses organization default
        currency: selectedCompany?.currency?.id ?? null,
        attribute_values: filterAndProcessAttributeValues(
          data.deal_attribute_values ?? [],
          dealAttributes
        ),
      });

      track(`Click: Create deal submit (${sourceItem})`);
    } catch (error) {
      if (error instanceof DisconnectedError) return;
      const axiosError = error as ApiError;
      const apiMessage = ToastService.getErrorMessage(axiosError);
      const messages = Array.isArray(apiMessage) ? apiMessage : [apiMessage];

      if (messages.includes(UPLOAD_ERROR_MESSAGES.IC_MEMO_CIM_DUPLICATE)) {
        setDealError(UPLOAD_ERROR_MESSAGES.IC_MEMO_CIM_DUPLICATE);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    reset();
    closeDialog();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(handleSave)().catch(() => {});
    }
  };

  useEscapeKey({ callback: closeDialog, enabled: isOpen, ignoreWhenFocusedOnInput: true });

  // Clear deal error when selecting another document
  useEffect(() => {
    setDealError(null);
  }, [formData.documents, formData.attachments]);

  useEffect(() => {
    // refetch attachments if some documents are not uploaded
    if (!isProcessingDocuments) return;

    const intervalId = setInterval(() => {
      void refetchThreadAttachments();
    }, REFETCH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [isProcessingDocuments, refetchThreadAttachments]);

  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      titleInputRef.current.focus({ preventScroll: true });
    }
  }, [isOpen]);

  useEffect(() => {
    if (
      !formData.stage ||
      (!formData.company && formData.stage?.status === DealStageStatus.PORTFOLIO)
    ) {
      const options = stageOptions[0]?.options;
      if (!options || options.length === 0) return;

      const defaultStage = options.find((p) => p.is_default) || options[0];
      setValue("stage", defaultStage as IPipelineStageSelectOption);
    }
  }, [stageOptions]);

  useEffect(() => {
    // Trigger validation on mount if query is provided
    if (query) {
      setValue("title", query, { shouldDirty: true });
    }
  }, []);

  return (
    <BaseDialog
      key={query}
      isFullScreen
      title="New Deal"
      initialFocus={firstInputRef}
      bodyClassName="min-h-[calc(100vh_-_62px)]"
    >
      <form
        noValidate
        className="h-[calc(100vh_-_78px)] w-full flex flex-col overflow-y-auto"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-4 px-4 flex-1">
          <ControlledInput
            control={control}
            name="title"
            label="Name*"
            onKeyDown={handleKeyDown}
            errors={formState.errors}
            inputRef={titleInputRef}
          />

          {/* Deal sourcing themes */}
          {isAvailable("THEMATIC_SOURCING") && (
            <SourcingThemeFormField<ICreateDealForm> control={control} setValue={setValue} />
          )}

          <PipelineStageFormField<ICreateDealForm>
            control={control}
            options={stageOptions}
            showGroupHeaders={true}
          />

          <CompanyFormField control={control} />

          {isDealSourceAvailable && (
            <>
              <SourceTypeFormField control={control} />
              <SourceCompanyFormField control={control} />
              <SourcePersonFormField control={control} />
            </>
          )}

          <TeamField
            control={control}
            options={userOptions}
            label="Deal Team"
            footerRef={footerRef}
          />

          {/* The deal's own attributes — submitted with it, since the deal doesn't exist yet. */}
          {dealAttributes.length > 0 && (
            <CollapsibleSection title="Deal Details" className="mb-4">
              <AttributesForm<ICreateDealForm>
                name="deal_attribute_values"
                control={control}
                attributes={dealAttributes}
                currency={selectedCompany?.currency}
                commonAttributeIds={commonAttributeIds}
                hideCommonAttributeIcons={!companyField}
              />
            </CollapsibleSection>
          )}

          {/* The selected company's attributes — shared ones mirror into the deal's fields above. */}
          {showCompanyAttributes && companyAttributes.length > 0 && (
            <CollapsibleSection title="Company Details" className="mb-4">
              <AttributesForm<ICreateDealForm>
                name="attribute_values"
                control={control}
                attributes={companyAttributes}
                currency={selectedCompany?.currency}
                commonAttributeIds={commonAttributeIds}
                // A company can't be tagged into its own Data Objects field.
                excludedRelationIds={selectedCompany ? [selectedCompany.id] : undefined}
                onCommonAttributeClick={
                  effectiveDeal ? () => openDealPage(effectiveDeal.id) : undefined
                }
              />
            </CollapsibleSection>
          )}
        </div>

        <div
          ref={footerRef}
          className="sticky bottom-0 z-[1] flex justify-between border-t-2 border-stroke-primary dark:border-gray-600 pt-4 px-4 bg-white dark:bg-gray-800"
        >
          <div className="w-full flex flex-col gap-2">
            <CIMUploader
              control={control}
              setValue={setValue}
              attachments={threadAttachments}
              apiError={dealError}
            />

            <Button
              disabled={disableSubmitButton}
              onClick={handleSubmit(handleSave)}
              buttonType="PRIMARY"
            >
              Add Deal
            </Button>

            <Button
              className="disabled:bg-white80 disabled:opacity-50 dark:disabled:hover:bg-gray-700"
              onClick={handleDialogClose}
              buttonType="CANCEL"
            >
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </BaseDialog>
  );
}
